import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, ArrowUpRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { useMeets } from "@/hooks/useMeets";
import { fetchMeets } from "@/lib/strapi";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function MeetsPage() {
  const [filterCountry, setFilterCountry] = useState("All");
  const [countryOptions, setCountryOptions] = useState(["All"]);
  const { meets, isLoading, isLoadingMore, error, hasMore, loadMore } = useMeets(500, 12, filterCountry);
  console.log(meets);
  
  const getMeetPath = (meet) => `/meets/${meet?.slug || meet?.id}`;

  const getMeetCountry = (meet) => meet?.country || meet?.location || "";

  useEffect(() => {
    let mounted = true;

    const loadCountryOptions = async () => {
      try {
        const allMeets = await fetchMeets({ limit: 500 });
        if (!mounted) {
          return;
        }
        // console.log("allMeets", allMeets);
        const countries = Array.from(
          new Set(allMeets.map((meet) => getMeetCountry(meet)).filter(Boolean))
        );        
        // console.log("countries", countries);
        setCountryOptions(["All", ...countries]);
      } catch (_error) {
        if (mounted) {
          setCountryOptions(["All"]);
        }
      }
    };

    loadCountryOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const displayedMeets = useMemo(() => {
    return [...meets].sort((a, b) => {
      const timeA = new Date(a.startDate).getTime();
      const timeB = new Date(b.startDate).getTime();
      // Use 0 as fallback if date is invalid, though most should be valid
      return (Number.isNaN(timeA) ? 0 : timeA) - (Number.isNaN(timeB) ? 0 : timeB);
    });
  }, [meets]);

  const getDateBadge = (value) => {
    const text = (value || "").trim();
    if (!text) {
      return { top: "Date", bottom: "TBA" };
    }

    const [top, bottom] = text.includes(",") ? text.split(",") : [text, ""];
    return { top: top?.trim() || "Date", bottom: bottom?.trim() || "" };
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="meets-page" className="min-h-screen">
      <Header />
      <PageBanner
        eyebrow="Competition Meets"
        title="Upcoming Competitions"
        description="Stay informed about swim meets and competitions across the APAC region and beyond."
        image="https://images.unsplash.com/photo-1519315901367-f34ff9154487?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
      />

      <section className="bg-amber-pale py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {!isLoading && !error && meets.length > 0 && (
            <div className="mb-12">
              <label className="font-inter font-light text-xs tracking-[0.15em] uppercase text-warm-gray mb-3 block">
                Filter by Country
              </label>
              <div className="flex flex-wrap gap-2" data-testid="meets-country-filter">
                {countryOptions.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilterCountry(c)}
                    data-testid={`meets-filter-${c.toLowerCase().replace(/\s/g, '-')}`}
                    className={`font-inter font-medium text-xs px-4 py-2 rounded-sm border transition-colors duration-150 ${
                      filterCountry === c
                        ? "bg-solar-orange text-near-black border-solar-orange"
                        : "bg-transparent text-near-black/50 border-near-black/10 hover:border-near-black/30 hover:text-near-black"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {isLoading && (
              <div className="bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                Loading meets...
              </div>
            )}

            {!!error && !isLoading && (
              <div className="bg-white rounded-sm p-6 text-sm font-inter text-red-600">
                Unable to load meets right now. Please try again shortly.
              </div>
            )}

            {!isLoading && !error && meets.length === 0 && (
              <div className="bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                No meets found.
              </div>
            )}

            {!isLoading && !error && filterCountry !== "All" && meets.length === 0 && (
              <div className="bg-white rounded-sm p-6 text-sm font-inter text-warm-gray">
                No meets found for the selected country. Try a different country.
              </div>
            )}

            {!isLoading && !error && displayedMeets.map((meet) => (
              <motion.div key={meet.id} variants={itemVariants}>
                <Link
                  to={getMeetPath(meet)}
                  data-testid={`meet-card-${meet.id}`}
                  className="group bg-white rounded-sm border border-near-black/5 hover:border-amber-dusk/20 transition-colors duration-200 overflow-hidden"
                >
                  <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                    {/* Date Badge */}
                    <div className="flex-shrink-0 bg-near-black rounded-sm p-5 text-center min-w-[100px]">
                      <Calendar size={20} className="text-solar-orange mx-auto mb-2" />
                      <p className="font-inter font-semibold text-sm text-white leading-tight">
                        {getDateBadge(meet.date).top}
                      </p>
                      <p className="font-inter font-light text-[10px] text-white/50 mt-1">
                        {getDateBadge(meet.date).bottom}
                      </p>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="font-inter font-semibold text-xl text-near-black tracking-tight group-hover:text-amber-dusk transition-colors duration-150">
                          {meet.name}
                        </h3>
                        <span className={`font-inter font-semibold text-[10px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm ${
                          meet.type === "International" ? "bg-solar-orange text-near-black" : meet.type === "National" ? "bg-amber-pale text-amber-dusk" : "bg-near-black/5 text-warm-gray"
                        }`}>
                          {meet.type}
                        </span>
                      </div>
                      <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed mb-3 line-clamp-2">
                        {meet.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-5">
                        <div className="flex items-center gap-1.5 text-warm-gray">
                          <MapPin size={13} />
                          <span className="font-inter font-light text-xs">{meet.location}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-warm-gray">
                          <Clock size={13} />
                          <span className="font-inter font-light text-xs">{meet.date}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-warm-gray">
                          <Users size={13} />
                          <span className="font-inter font-light text-xs">{meet.participants} athletes</span>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk group-hover:gap-2 transition-all duration-150">
                        Details
                        <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}

            {!isLoading && !error && hasMore && (
              <div className="pt-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="inline-flex items-center justify-center rounded-full bg-near-black px-6 py-3 font-inter text-sm text-white transition-colors duration-150 hover:bg-amber-dusk disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoadingMore ? "Loading more meets..." : "Load More Meets"}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
